/** Product search for the billing screen: every word must match the name,
 * brand, size, category or a voice alias; better matches first. */
import type { Product } from '../types';

export function normalise(s: string): string {
  return s.toLowerCase().replace(/[’'`.]/g, '').replace(/\s+/g, ' ').trim();
}

export function haystack(p: Product): string {
  return normalise([p.name, p.brand, p.size, p.size_ml ? `${p.size_ml}` : '', p.category, ...(p.voice_aliases || [])]
    .filter(Boolean).join(' '));
}

export function searchProducts(products: Product[], query: string, limit = 12): Product[] {
  const q = normalise(query);
  if (!q) return [];
  const words = q.split(' ');
  const scored: [number, Product][] = [];
  for (const p of products) {
    if (!p.is_active) continue;
    const h = haystack(p);
    if (!words.every((w) => h.includes(w))) continue;
    const name = normalise(p.name);
    let score = 0;
    if (name.startsWith(q)) score += 50;
    if (name.includes(q)) score += 20;
    if ((p.voice_aliases || []).some((a) => normalise(a) === q)) score += 60;
    words.forEach((w) => { if (h.split(' ').includes(w)) score += 5; });
    score -= name.length / 100;
    scored.push([score, p]);
  }
  return scored.sort((a, b) => b[0] - a[0]).slice(0, limit).map(([, p]) => p);
}

/** Find a product in the local cache by any of its barcodes (UPC-A 12 <-> 13 digits). */
export function findByBarcode(products: Product[], code: string): Product | undefined {
  const c = code.trim();
  const variants = new Set([c]);
  if (/^\d{12}$/.test(c)) variants.add(`0${c}`);
  if (/^0\d{12}$/.test(c)) variants.add(c.slice(1));
  return products.find((p) => p.barcodes.some((b) => variants.has(b.code)));
}
